import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, User, LogOut, BookOpen, Receipt, Shield, Gift, Heart, ShoppingCart, Bookmark } from "lucide-react";
import StreakBadge from "@/components/StreakBadge";
import ThemeToggle from "@/components/ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const { count: cartCount } = useCart();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="site-header"
      className="backdrop-blur-xl bg-paper/80 border-b border-edge sticky top-0 z-50"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-terracotta" strokeWidth={1.25} />
          <span className="font-serif text-2xl tracking-tight">Folio</span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {[
            { to: "/", label: "Home" },
            { to: "/browse", label: "Browse" },
            { to: "/collections", label: "Collections" },
            { to: "/library", label: "Library" },
          ].map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.25em] font-medium transition-colors ${
                  isActive ? "text-terracotta" : "text-ink hover:text-terracotta"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <ThemeToggle />
          <StreakBadge />
          <Link
            to="/cart"
            data-testid="nav-cart"
            className="relative flex items-center text-ink hover:text-terracotta transition-colors"
            aria-label={`Cart (${cartCount} items)`}
          >
            <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
            {cartCount > 0 && (
              <span
                data-testid="cart-badge"
                className="absolute -top-2 -right-2 bg-terracotta text-paper text-[10px] font-medium min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full"
              >
                {cartCount}
              </span>
            )}
          </Link>
          {user && user !== false ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 text-xs uppercase tracking-widest"
                >
                  <User className="w-4 h-4" strokeWidth={1.5} />
                  <span className="hidden sm:inline truncate max-w-[120px]">{user.name || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-paper border border-edge rounded-none">
                <DropdownMenuItem onClick={() => navigate("/library")} data-testid="menu-library">
                  <ShoppingBag className="w-4 h-4 mr-2" /> My Library
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/favorites")} data-testid="menu-favorites">
                  <Heart className="w-4 h-4 mr-2" /> Favorites
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/highlights")} data-testid="menu-highlights">
                  <Bookmark className="w-4 h-4 mr-2" /> Highlights
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">
                  <Receipt className="w-4 h-4 mr-2" /> Orders
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/referrals")} data-testid="menu-referrals">
                  <Gift className="w-4 h-4 mr-2" /> Referrals
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <Shield className="w-4 h-4 mr-2" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-xs uppercase tracking-[0.25em] font-medium">
                Sign in
              </Link>
              <Link to="/register" data-testid="nav-register" className="btn-primary !py-3 !px-5">
                Join
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
